import { useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Chip, Paper, TextField, Typography } from '@mui/material';
import api from '../services/api';
import { safeDecode } from '../utils/decode';
import { wranglerError } from './errors';
import { useWranglerQuestions } from './useWranglerData';

const WRANGLER_FIELDS = { sid: "Wrangler's session ID (sid)", quota_status: "Wrangler's quota_status" };

/** "Q16: sess id", or "Q4: Grid - Row A" for a grid row. */
const label = (question, byId) => {
  const parent = byId.get(question.parent_question_id);
  const title = safeDecode(question.title);
  return `Q${question.question_id}: ${parent ? `${safeDecode(parent.title)} - ${title}` : title}`;
};

/**
 * The Setup tab's list of questions kept out of quota building: Wrangler's own Hidden Values,
 * found automatically, and any others hidden by hand (e.g. a Hidden Value that only tracks
 * the panel). Hidden questions don't appear in the condition builder, the set builder or the
 * spreadsheet template.
 */
const HiddenQuestions = ({ survey }) => {
  const { questions, loading, reload } = useWranglerQuestions(survey.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const byId = useMemo(() => new Map(questions.map((q) => [q.question_id, q])), [questions]);
  const automatic = questions.filter((q) => WRANGLER_FIELDS[q.hidden_from_quotas]);
  const byHand = questions.filter((q) => q.hidden_from_quotas === 'hidden');
  const candidates = questions.filter((q) => q.usable_in_quotas);

  const save = async (ids) => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/surveys/${survey.id}/wrangler/hidden-questions/`, { question_ids: ids });
      await reload();
    } catch (err) {
      setError(wranglerError(err));
    } finally {
      setBusy(false);
    }
  };
  const ids = byHand.map((q) => q.question_id);

  if (loading && !questions.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={600} sx={{ mb: 1 }}>
        Questions hidden from quota building
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These don&apos;t appear when building quotas, quota sets, rules or the spreadsheet template. Wrangler&apos;s own
        Hidden Values are hidden automatically; hide any other question that isn&apos;t for quotas.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {automatic.map((q) => (
          <Chip key={q.question_id} label={`${label(q, byId)} · ${WRANGLER_FIELDS[q.hidden_from_quotas]}`} />
        ))}
        {byHand.map((q) => (
          <Chip key={q.question_id} label={label(q, byId)} variant="outlined" disabled={busy}
            onDelete={() => save(ids.filter((id) => id !== q.question_id))} />
        ))}
        {!automatic.length && !byHand.length && (
          <Typography variant="body2" color="text.secondary">
            None. Wrangler&apos;s Hidden Values weren&apos;t found in Alchemer&apos;s copy of the survey: if they exist under
            other names, hide them below.
          </Typography>
        )}
      </Box>
      <Autocomplete
        options={candidates}
        getOptionLabel={(q) => label(q, byId)}
        value={null}
        blurOnSelect
        disabled={busy}
        onChange={(e, q) => q && save([...ids, q.question_id])}
        renderInput={(params) => <TextField {...params} size="small" label="Hide a question" />}
        sx={{ maxWidth: 480 }}
      />
    </Paper>
  );
};

export default HiddenQuestions;
